import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from '@shared/components/ErrorFallback';

/**
 * ErrorBoundaryRoute Component
 * Wraps a route component with an error boundary for better error isolation
 * This ensures that if one route crashes, it doesn't crash the entire app
 */
function ErrorBoundaryRoute({ children }) {
  // Helper to check if error is ResizeObserver related (harmless)
  const isResizeObserverError = (error) => {
    if (!error) return false;
    const message = String(error?.message || error?.toString() || '').toLowerCase();
    const stack = String(error?.stack || '').toLowerCase();
    const combined = `${message} ${stack}`;
    
    return (
      combined.includes('resizeobserver') ||
      combined.includes('resizeobserver loop') ||
      combined.includes('resizeobserver loop limit exceeded') ||
      combined.includes('resizeobserver loop completed') ||
      combined.includes('resizeobserver loop completed with undelivered notifications') ||
      combined.includes('resizeobserver loop limit') ||
      combined.includes('undelivered notifications') ||
      combined.includes('crbug/1173575')
    );
  };

  const logError = (error, errorInfo) => {
    // Suppress ResizeObserver errors - they're harmless browser quirks
    if (isResizeObserverError(error)) {
      return; // Don't log or handle ResizeObserver errors
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.error('Route error:', error);
      console.error('Error info:', errorInfo);
    }
    // In production, you might want to send errors to an error tracking service
  };

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={logError}
      onReset={() => {
        // Reset by reloading the page
        window.location.reload();
      }}
    >
      {children}
    </ErrorBoundary>
  );
}

export default ErrorBoundaryRoute;
